import type { Article } from "./types";

export const orgtoolsVsAsana: Article = {
  slug: "orgtools-vs-asana",
  kind: "comparison",
  title: "Orgtools vs Asana: Decision Record vs Project Management",
  description:
    "Asana coordinates tasks and projects. Orgtools records the decisions above execution: the reasoning, owner, and outcome. How the two compare and when to use each.",
  lede: "Asana is one of the most widely used project and work management tools. Orgtools is not a project tracker. It shows the calls inside the work: what your people and agents decided, by whom, on what reasoning, and which ones need you.",
  datePublished: "2026-06-26",
  sections: [
    {
      type: "prose",
      heading: "What Sets Orgtools Apart from Asana",
      body: "Asana is built to coordinate work: tasks, projects, assignees, due dates, and the workflows that move them along. It answers what needs to get done and who is doing it. It does not capture why a direction was chosen, the reasoning weighed, or whether the call turned out to be right.\n\nOrgtools sits above that layer. It finds the decisions a plan depends on and shows who decided what, wherever they decided it, including in an agent session. The calls that need a leader run through a room with a named person deciding, and Orgtools watches what happens after. Asana tells you the tasks shipped. Orgtools tells you which calls shipped them, and whether they were sound.",
    },
    {
      type: "table",
      heading: "Head-to-Head Comparison",
      columns: ["Capability", "Orgtools", "Asana"],
      rows: [
        ["Structured decision record", "Yes", "Tasks and projects, not decisions"],
        ["Captures reasoning and tradeoffs", "Yes", "Comments and descriptions only"],
        ["Named accountability for the call", "Yes", "Task assignees, not decision owners"],
        ["Outcome tracked against the decision", "Yes", "Task completion status"],
        ["Drift signals after the decision", "Yes", "No"],
        ["Execution and task coordination", "Hands off to your tools", "Core strength"],
      ],
    },
    {
      type: "prose",
      heading: "When to Choose Asana",
      body: "Choose Asana when the central problem is execution coordination: managing tasks, projects, and team workflows at scale, with a deep integration ecosystem. If your teams need to track who is doing what by when, Asana is purpose-built for it and Orgtools is not trying to replace that.",
    },
    {
      type: "prose",
      heading: "When to Choose Orgtools",
      body: "Choose Orgtools when the problem is the calls inside the work: your people and agents ship faster than anyone can review, decisions get made in sessions and threads, and the reasoning evaporates. Execution failures often trace back to a call nobody saw, not to a task-tracking gap. The two tools coexist cleanly: coordinate the work in Asana, keep your judgment on the calls in Orgtools.",
    },
    {
      type: "cta",
      heading: "See the Calls Behind Your Tasks",
      href: "/platform",
      label: "Explore the Platform",
    },
  ],
  faq: [
    {
      question: "Is Orgtools a replacement for Asana?",
      answer:
        "No. Asana coordinates tasks and projects. Orgtools shows the decisions behind that work and puts the ones that matter in front of you. Most teams that use both keep Asana for execution and Orgtools for the calls that set the direction.",
    },
    {
      question: "Can Orgtools track tasks and projects?",
      answer:
        "That is not its job. Orgtools is the system of record for decisions and their outcomes. It hands execution off to the tools your teams already use, and pulls context back from them into the fact base behind each decision.",
    },
    {
      question: "What does Orgtools capture that Asana does not?",
      answer:
        "The reasoning behind a decision, the people accountable for it, the data it rested on, and whether the outcome matched the intent. Asana captures the work that follows a decision, not the decision itself.",
    },
  ],
};
